import { ApiError } from '@jq4race/api-client';
import { translateApiError, useTranslation } from '@jq4race/i18n';
import { Button, InputOTP, InputOTPGroup, InputOTPSlot, Label } from '@jq4race/ui';
import { useNavigate } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { Session } from '../../domain/entities/session';
import { useAuthServices } from '../auth-services-context';
import { useAuthState } from '../auth-state-context';

const OTP_LENGTH = 6;
const OTP_SLOT_IDS = Array.from({ length: OTP_LENGTH }, (_, i) => `email-otp-slot-${i}`);
const RESEND_COOLDOWN_SECONDS = 60;

/** Extracts the server-provided cooldown from a 429 mfa_email_otp_cooldown response. */
function cooldownFromError(err: unknown): number | null {
  if (err instanceof ApiError && err.status === 429) {
    const details = err.details as { retryAfterSeconds?: number } | null;
    return details?.retryAfterSeconds ?? RESEND_COOLDOWN_SECONDS;
  }
  return null;
}

interface AlertProps {
  children: React.ReactNode;
}

function Alert({ children }: AlertProps) {
  return (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-sm border border-destructive/60 bg-destructive/15 px-3 py-2 text-[13px] font-medium text-destructive"
    >
      <AlertTriangle className="mt-0.5 size-4 text-destructive" />
      <span>{children}</span>
    </div>
  );
}

interface EmailOtpFormProps {
  /** The pending MFA token — same one used for the TOTP verify/setup paths. */
  mfaToken: string;
  /** Email used for session hydration; matches verifyMfa's/activateMfa's contract. */
  email: string;
  /** Called with the full session once the emailed code is verified. */
  onVerified: (session: Session) => void;
  /** Optional escape hatch back to the calling page's other MFA method. */
  onBack?: () => void;
  backLabel?: string;
}

/**
 * Shared email-OTP entry state used by both LoginMfaPage (as an alternative
 * to an already-enrolled TOTP code) and SetupMfaPage (as a way to skip TOTP
 * enrollment for now). Requests a code exactly once on mount — i.e. the
 * moment the caller switches into this state, never on every render — and
 * offers a 60s-cooldown resend action.
 */
export function EmailOtpForm({
  mfaToken,
  email,
  onVerified,
  onBack,
  backLabel,
}: EmailOtpFormProps) {
  const { t } = useTranslation('auth');
  const auth = useAuthServices();
  const { setMfaIntent } = useAuthState();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [codeState, setCodeState] = useState<'idle' | 'invalid' | 'expired'>('idle');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const requestedRef = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: requests exactly once on mount (guarded by requestedRef) — mfaToken/auth are stable for the lifetime of this form
  useEffect(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;
    let cancelled = false;
    auth
      .requestEmailOtp({ mfaToken })
      .then(() => {
        if (!cancelled) setCooldown(RESEND_COOLDOWN_SECONDS);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const retryAfterSeconds = cooldownFromError(err);
        if (retryAfterSeconds !== null) {
          setCooldown(retryAfterSeconds);
        } else {
          setError(err);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  async function handleResend() {
    if (cooldown > 0 || isResending) return;
    setError(null);
    setCodeState('idle');
    setIsResending(true);
    try {
      await auth.requestEmailOtp({ mfaToken });
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      const retryAfterSeconds = cooldownFromError(err);
      if (retryAfterSeconds !== null) {
        setCooldown(retryAfterSeconds);
      } else {
        setError(err);
      }
    } finally {
      setIsResending(false);
    }
  }

  /**
   * Escape hatch for when the initial code request fails and the user would
   * otherwise be stranded on this screen (e.g. an already-expired pending
   * token → 401/422 with nothing else actionable). Clears the mfa intent the
   * same way a fresh session does (see setSession in auth-store.ts) so a
   * stale token can't be resubmitted after navigating back to /login.
   */
  function handleBackToSignIn() {
    setMfaIntent(null);
    void navigate({ to: '/login' });
  }

  async function verify(value: string) {
    if (value.length !== OTP_LENGTH || isVerifying) return;
    setError(null);
    setCodeState('idle');
    setIsVerifying(true);
    try {
      const session = await auth.verifyEmailOtp({ mfaToken, code: value, email });
      onVerified(session);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setCodeState('invalid');
      } else if (err instanceof ApiError && err.status === 422) {
        setCodeState('expired');
      } else {
        setError(err);
      }
      setCode('');
    } finally {
      setIsVerifying(false);
    }
  }

  return (
    <div className="grid gap-5">
      {codeState === 'invalid' ? (
        <Alert>{t('emailOtp.invalidCode')}</Alert>
      ) : codeState === 'expired' ? (
        <Alert>{t('emailOtp.expiredCode')}</Alert>
      ) : error ? (
        <Alert>
          {error instanceof ApiError ? translateApiError(error, t) : t('apiErrors:codes.unknown')}
        </Alert>
      ) : null}
      <p className="text-[13px] text-jq-graphite">{t('emailOtp.sentTo', { email })}</p>
      <div className="grid gap-3">
        <Label htmlFor="email-otp-code">{t('emailOtp.codeLabel')}</Label>
        <InputOTP
          id="email-otp-code"
          maxLength={OTP_LENGTH}
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="^[0-9]+$"
          value={code}
          disabled={isVerifying}
          onChange={(value) => {
            setCode(value);
            void verify(value);
          }}
          containerClassName="w-full"
        >
          <InputOTPGroup className="w-full gap-2 sm:gap-3">
            {OTP_SLOT_IDS.map((id, i) => (
              <InputOTPSlot key={id} index={i} className="h-14 flex-1 text-[22px] sm:h-16" />
            ))}
          </InputOTPGroup>
        </InputOTP>
        <p className="text-[12px] text-jq-graphite">{t('emailOtp.expiryTip')}</p>
      </div>
      <Button
        type="button"
        variant="link"
        className="justify-self-start px-0"
        disabled={cooldown > 0 || isResending}
        onClick={handleResend}
      >
        {cooldown > 0
          ? t('emailOtp.resendCountdown', { seconds: cooldown })
          : isResending
            ? t('emailOtp.resending')
            : t('emailOtp.resend')}
      </Button>
      {onBack ? (
        <Button type="button" variant="link" className="justify-self-start px-0" onClick={onBack}>
          {backLabel ?? t('emailOtp.back')}
        </Button>
      ) : null}
      <Button
        type="button"
        variant="link"
        className="justify-self-start px-0 text-jq-graphite"
        onClick={handleBackToSignIn}
      >
        {t('emailOtp.backToSignIn')}
      </Button>
    </div>
  );
}
